// PurchaseTrialCredit.tsx
import { Routes, CheckoutType } from 'types/global.type';
import { useEffect, useState } from 'react';
import { redirectToCheckout } from 'utils/createTrial';
import { useLazyStripeConfigQuery } from 'redux/services/payment';
import { useLazyCheckoutQuery } from 'redux/services/workspace';
import useAuth from 'hooks/useAuth';
import Button from 'components/global/Button';
import Tooltip from 'components/global/Tooltip';
import drawerNavStyles from 'components/global/Header/subcomponents/DrawerNav/drawerNav.module.scss';
import getStorageMethod from 'utils/helpers/getStorageMethod';
import PurchaseChoiceModal from 'components/create-trial/PurchaseChoiceModal';
import getErrorMessage from 'utils/helpers/getErrorMessage';

interface PurchaseTrialCreditProps {
  disablePurchase?: boolean;
}

export const makePayment = (
  fetchProfile: () => void,
  currentUser: any,
  getConfigKey: () => void,
  setErrorMessage: (msg: string) => void,
  setModalOpen: (open: boolean) => void,
) => {
  fetchProfile();
  if (currentUser?.category === 'partner') {
    setErrorMessage('This feature is not available for partner users');
  } else {
    getConfigKey();
    setModalOpen(true);
  }
};

export const handleModalConfirm = (
  getConfigKey: () => void,
  checkout: Function,
  type: CheckoutType,
) => {
  getConfigKey();
  const submitData = new FormData();
  submitData.append('pre_purchase', 'true');
  checkout({ type, formData: submitData });
};

const PurchaseTrialCredit = ({ disablePurchase }: PurchaseTrialCreditProps) => {
  const [purchaseChoiceModalOpen, setPurchaseChoiceModalOpen] = useState(false);
  const [getConfigKey, { data: configData, isLoading }] =
    useLazyStripeConfigQuery();
  const [
    checkout,
    { data: checkoutData, isLoading: checkoutLoading, error, isError },
  ] = useLazyCheckoutQuery();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const { isGuest, user, fetchProfile } = useAuth();
  const [currentUser, setCurrentUser] = useState(user);

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  useEffect(() => {
    if (user) {
      setCurrentUser(user);
    }
  }, [user]);

  useEffect(() => {
    if (configData && checkoutData) {
      const { publishableKey } = configData;
      const { sessionId } = checkoutData.data;
      const storage = getStorageMethod();

      if (storage === 'session') {
        window.open(
          `${Routes.purchaseTrial}?key=${publishableKey}&sessionId=${sessionId}`,
          '_self',
        );
      } else {
        redirectToCheckout(publishableKey, sessionId);
      }
    }
  }, [configData, checkoutData]);

  useEffect(() => {
    if (isError) {
      const errorObj: any = error;
      setErrorMessage(getErrorMessage(errorObj));
    }
  }, [error, isError]);

  return (
    <>
      <Tooltip
        label={
          isGuest
            ? 'First create an account to enable purchasing trial credits.'
            : ''
        }
      >
        <Button
          action="button"
          variant="brand"
          className={drawerNavStyles['drawer-nav__try-farmtest']}
          isLoading={isLoading || checkoutLoading}
          isDisabled={isGuest || disablePurchase}
          onClick={() =>
            makePayment(
              fetchProfile,
              currentUser,
              getConfigKey,
              setErrorMessage,
              setPurchaseChoiceModalOpen,
            )
          }
          data-testid="purchase-trial-credit"
        >
          BUY TRIAL CREDITS
        </Button>
      </Tooltip>

      <PurchaseChoiceModal
        isOpen={purchaseChoiceModalOpen}
        isLoading={checkoutLoading}
        optionsLoading={isLoading}
        options={configData?.prices || []}
        headerText="Purchase Options"
        closeModal={() => setPurchaseChoiceModalOpen(false)}
        confirmAction={(type: CheckoutType) =>
          handleModalConfirm(getConfigKey, checkout, type)
        }
        error={isError ? errorMessage : null}
      />
    </>
  );
};

export default PurchaseTrialCredit;
